/**
 * @fileoverview Form state and handlers for CommercialOffer section.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { logger, sanitizeError } from "../../../utils/logger.js";
import {
  getExportsOptions,
  getImportsOptions,
  getSellsToGovernmentOptions,
  getCompanyGeneralActivityOptions,
  getCountryExportingToOptions,
  getCountryImportingFromOptions,
} from "../../../data/selectOptions";
import { useFieldValidation } from "../../../hooks/useFieldValidation";

const EMPTY_LOCALIZED_PRODUCTS = { es: "", en: "", fr: "" };

const normalizePrincipalProducts = (rawProducts) => {
  try {
    const companyPrincipalProducts = rawProducts || {};
    ["es", "en", "fr"].forEach((lang) => {
      if (!companyPrincipalProducts[lang]) {
        companyPrincipalProducts[lang] = "";
      }
    });
    return companyPrincipalProducts;
  } catch (error) {
    logger.error("Error parsing principal products:", {
      err: sanitizeError(error),
    });
    return { ...EMPTY_LOCALIZED_PRODUCTS };
  }
};

export const useCommercialOfferForm = ({
  company,
  onFieldChange,
  onValidationChange,
}) => {
  const { t, i18n } = useTranslation();
  const prevHasErrorsRef = useRef(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);

  const { hasErrors } = useFieldValidation({
    productoPrincipal: "",
    materiaPrima: "",
    certificacionesDeCalidad: "",
  });

  const [principalProducts, setPrincipalProducts] = useState(() =>
    normalizePrincipalProducts(company?.productoPrincipal),
  );

  const companyPrincipalProducts = useMemo(() => {
    if (company?.productoPrincipal) {
      return company.productoPrincipal;
    }
    return { ...EMPTY_LOCALIZED_PRODUCTS };
  }, [company?.productoPrincipal]);

  useEffect(() => {
    setPrincipalProducts(companyPrincipalProducts);
  }, [companyPrincipalProducts]);

  useEffect(() => {
    setCurrentLanguage(i18n.language);
  }, [i18n.language]);

  useEffect(() => {
    if (onValidationChange && prevHasErrorsRef.current !== hasErrors) {
      prevHasErrorsRef.current = hasErrors;
      onValidationChange(hasErrors);
    }
  }, [hasErrors, onValidationChange]);

  const handleInputChange = useCallback(
    (field, value) => {
      if (onFieldChange) {
        onFieldChange(field, value);
      }
    },
    [onFieldChange],
  );

  const handlePrincipalProductsChange = useCallback(
    (value) => {
      const newPrincipalProducts = {
        ...principalProducts,
        [currentLanguage]: value,
      };
      setPrincipalProducts(newPrincipalProducts);
      handleInputChange("productoPrincipal", newPrincipalProducts);
    },
    [currentLanguage, handleInputChange, principalProducts],
  );

  const [exports, setExports] = useState(company?.exporta || null);
  const [imports, setImports] = useState(company?.importa || null);
  const [sellsToGovernment, setSellsToGovernment] = useState(
    company?.vendeAGobierno || null,
  );
  const [countryExportingTo, setCountryExportingTo] = useState(
    company?.paisExportacion || null,
  );
  const [countryImportingFrom, setCountryImportingFrom] = useState(
    company?.paisImportacion || null,
  );

  const updateCompanyField = useCallback((field, value) => {
    if (company) {
      company[field] = value;
    }
  }, [company]);

  const handleExportsChange = useCallback(
    (selectedOption) => {
      updateCompanyField("exporta", selectedOption.value);
      setExports(selectedOption.value);
      handleInputChange("exporta", selectedOption.value);
    },
    [handleInputChange, updateCompanyField],
  );

  const handleCountryExportChange = useCallback(
    (selectedOption) => {
      updateCompanyField("paisExportacion", selectedOption.value);
      setCountryExportingTo(selectedOption.value);
      handleInputChange("paisExportacion", selectedOption.value);
    },
    [handleInputChange, updateCompanyField],
  );

  const handleImportsChange = useCallback(
    (selectedOption) => {
      updateCompanyField("importa", selectedOption.value);
      setImports(selectedOption.value);
      handleInputChange("importa", selectedOption.value);
    },
    [handleInputChange, updateCompanyField],
  );

  const handleCountryImportChange = useCallback(
    (selectedOption) => {
      updateCompanyField("paisImportacion", selectedOption.value);
      setCountryImportingFrom(selectedOption.value);
      handleInputChange("paisImportacion", selectedOption.value);
    },
    [handleInputChange, updateCompanyField],
  );

  const handleSellsToGovernmentChange = useCallback(
    (selectedOption) => {
      updateCompanyField("vendeAGobierno", selectedOption.value);
      setSellsToGovernment(selectedOption.value);
      handleInputChange("vendeAGobierno", selectedOption.value);
    },
    [handleInputChange, updateCompanyField],
  );

  return {
    t,
    exportsOptions: getExportsOptions(t),
    importsOptions: getImportsOptions(t),
    sellsToGovernmentOptions: getSellsToGovernmentOptions(t),
    countryExportingToOptions: getCountryExportingToOptions(t),
    countryImportingFromOptions: getCountryImportingFromOptions(t),
    exports,
    imports,
    sellsToGovernment,
    countryExportingTo,
    countryImportingFrom,
    handleExportsChange,
    handleCountryExportChange,
    handleImportsChange,
    handleCountryImportChange,
    handleSellsToGovernmentChange,
    handlePrincipalProductsChange,
    principalProducts,
  };
};
